/* eslint-disable react/prop-types */
//* External
import { omit } from 'lodash';
import { useState } from 'react';
import Offcanvas from 'react-bootstrap/Offcanvas';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

//* App Custom
import { tnl } from '@i18nApp/i18n';
import { cleanObject, obtainParams, verifyData } from '@utilsApp/index';
import { Button, InputHandler } from '../index';
import './offCanvasForm.scss';

// Add the name(s) of the filter(s) to be rendered outside Offcanvas.
const ALWAYS_VISIBLE_INPUTS = [
  'document_number',
  'start_date',
  'validation_status',
  'application_status'
];

const Form = ({
  type,
  inputs = [],
  control,
  handleSubmit,
  onSubmit = () => {},
  showFilterButton = false,
  onFilterClick = () => {},
  resetFilters = () => {},
  resetTimestamp = 0
}) => {
  const { isDoingRequest } = useSelector((state) => state.commonState);

  const buttons = (
    <>
      <Button
        type="submit"
        variant="primary"
        border="none"
        title={type !== 'offcanvas' ? 'buttons.filter' : ''}
        text={type === 'offcanvas' ? 'buttons.filter' : ''}
        iconName="magnifyingGlass"
        disabled={isDoingRequest}
        fullWidth={type === 'offcanvas'}
      />
      <Button
        type="button"
        variant="secondary"
        border="none"
        title={type !== 'offcanvas' ? 'buttons.remove_filters' : ''}
        text={type === 'offcanvas' ? 'buttons.remove_filters' : ''}
        iconName="remove"
        fullWidth={type === 'offcanvas'}
        onClick={resetFilters}
      />
    </>
  );

  return (
    <>
      {inputs.length > 0 && (
        <div className="searchRequest">
          <form onSubmit={handleSubmit(onSubmit)} className="row">
            {inputs
              .filter((input) => !input?.isHidden)
              .map((input) => (
                <InputHandler
                  key={`${input.name}_${resetTimestamp}`}
                  {...input}
                  label={type === 'offcanvas' ? input.label : ''}
                  control={control}
                  design={
                    type !== 'offcanvas'
                      ? `col-12 col-md-6 ${
                          input.type === 'date-range' ? 'col-lg-6' : 'col-lg-3'
                        } mb-3`
                      : 'col-12 mb-3'
                  }
                />
              ))}
            {type === 'offcanvas' ? (
              <div className="d-grid gap-2">{buttons}</div>
            ) : (
              buttons
            )}
            {showFilterButton && (
              <div className="col-auto">
                <Button
                  variant="secondary"
                  border="none"
                  text="labels.filters"
                  iconName="filter"
                  onClick={onFilterClick}
                />
              </div>
            )}
          </form>
        </div>
      )}
    </>
  );
};

const OffCanvasForm = ({
  inputs = () => [],
  inputParams = {},
  defaultValues = {},
  onSubmit = () => {},
  visibleInputCount
}) => {
  const [show, setShow] = useState(false);
  const [resetTimestamp, setResetTimestamp] = useState(Date.now());
  const [newDefaultValues, setNewDefaultValues] = useState(
    omit(defaultValues, ['length', 'page'])
  );

  const { control, handleSubmit, getValues, reset } = useForm({
    defaultValues: newDefaultValues
  });

  const resetFilters = () => {
    const emptyDefaults = {};
    const ts = Date.now();
    setNewDefaultValues(emptyDefaults);
    reset(emptyDefaults);
    setResetTimestamp(ts);
    onSubmit({});
  };

  const inputsToRender = inputs({
    ...inputParams,
    defaultValues: newDefaultValues
  });

  const getFilteredInputs = () => {
    if (visibleInputCount && visibleInputCount > 0) {
      const visibleInputs = inputsToRender.slice(0, visibleInputCount);
      const offcanvasInputs = inputsToRender.slice(visibleInputCount);
      return { visibleInputs, offcanvasInputs };
    } else {
      const visibleInputs = inputsToRender.filter((input) =>
        ALWAYS_VISIBLE_INPUTS.includes(input.name)
      );
      const offcanvasInputs = inputsToRender.filter(
        (input) => !ALWAYS_VISIBLE_INPUTS.includes(input.name)
      );
      return { visibleInputs, offcanvasInputs };
    }
  };

  const formSubmit = () => {
    const data = getValues();
    const cleanData = cleanObject(data);
    const paramsForVisual = obtainParams(cleanData, 'label');
    const paramsForRequest = obtainParams(cleanData, 'value');
    setNewDefaultValues(paramsForVisual);
    onSubmit(paramsForRequest);
  };

  const transformValue = (value) => {
    if (typeof value === 'string' && value.includes(',')) {
      return value
        .split(',')
        .map((item) => tnl(item))
        .join(', ');
    } else if (typeof value === 'object' && !Array.isArray(value)) {
      return Object.entries(value)
        .map(([key, val]) =>
          val !== undefined ? `${tnl(`labels.${key}`)}: ${val}` : null
        )
        .filter((item) => item !== null)
        .join(', ');
    } else {
      return tnl(value);
    }
  };

  const { visibleInputs, offcanvasInputs } = getFilteredInputs();

  return (
    <>
      {inputsToRender.length <= 3 ? (
        <div className="col-12 searchRequest">
          <Form
            type="simple"
            inputs={inputsToRender}
            control={control}
            handleSubmit={handleSubmit}
            onSubmit={formSubmit}
            resetFilters={resetFilters}
            resetTimestamp={resetTimestamp}
          />
        </div>
      ) : (
        <>
          <div className="d-flex flex-column gap-3">
            {visibleInputs.length > 0 && (
              <Form
                type="simple"
                inputs={visibleInputs}
                control={control}
                handleSubmit={handleSubmit}
                onSubmit={formSubmit}
                showFilterButton={offcanvasInputs.length > 0}
                onFilterClick={() => setShow(true)}
                resetFilters={resetFilters}
                resetTimestamp={resetTimestamp}
              />
            )}
            {visibleInputs.length === 0 && offcanvasInputs.length > 0 && (
              <div className="col-auto">
                <Button
                  variant="secondary"
                  border="none"
                  text="labels.filters"
                  iconName="filter"
                  onClick={() => setShow(true)}
                />
              </div>
            )}
            {Object.keys(newDefaultValues).length > 0 && (
              <div className="d-flex flex-wrap gap-2">
                {Object.entries(newDefaultValues).map(([key, value], index) => {
                  const transformedValue = transformValue(value);
                  if (!transformedValue) return null;
                  return (
                    <div
                      key={`filter_${index}`}
                      className="d-flex gap-1 offcanvas_applied_filter"
                    >
                      {verifyData(
                        [
                          /^data_filter_\d+(_to|_from)?$/.exec(key)
                            ? tnl('labels.customized_filter')
                            : tnl(`labels.${key}`, key),
                          transformedValue
                        ],
                        ': '
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <Offcanvas
            show={show}
            onHide={() => setShow(false)}
            placement="start"
            name="start"
            className="custom-width"
          >
            <div className="p-3">
              <Offcanvas.Header closeButton>
                <Offcanvas.Title className="fw-bold offcanvas_title">
                  {tnl('labels.filters')}
                </Offcanvas.Title>
              </Offcanvas.Header>
              <Offcanvas.Body>
                <div
                  style={{
                    overflowX: 'hidden',
                    overflowY: 'auto',
                    maxHeight: '85vh'
                  }}
                >
                  <Form
                    type="offcanvas"
                    inputs={offcanvasInputs}
                    control={control}
                    handleSubmit={handleSubmit}
                    onSubmit={formSubmit}
                    resetFilters={resetFilters}
                    resetTimestamp={resetTimestamp}
                  />
                </div>
              </Offcanvas.Body>
            </div>
          </Offcanvas>
        </>
      )}
    </>
  );
};

export default OffCanvasForm;
